import React from 'react';
import * as yup from 'yup';
import pluralize from 'pluralize';
import PollCard from './PollCard';
import type { TableCellProps } from 'react-virtualized';

import {
  InteractionDisplayState,
  InteractionInfo,
} from '$/components/Interactions/InteractionInfo';
import {
  InteractionModalAccordionsHook,
  InteractionModalComponentProps,
  ModalValidationRules,
} from '$/components/Interactions/types';
import { StyleProps } from '$/components/ui';
import { PlayerMode } from '$/components/WebinarPlayer/types';
import {
  AttendeeFragment,
  GetRegistrantsOrderBy,
  InteractionDetails,
  InteractionForAttendeeFragment,
  InteractionFragment,
  InteractionValueType,
  OptionField,
  PublicEWebinarFragment,
  ReactionFragment,
  ResultsAppearAt,
  RoomType,
  SelectionType,
} from '$/GraphQL/schema/schema';
import { PollIcon } from '$/images/FontAwesomeIcons';
import { surfaceAnswerText } from '$/sections/Home/Dashboard/Registrants/surfaceAnswerText';
import { RenderColumnData } from '$/sections/Home/Dashboard/Registrants/types';
import { AnalyticsCardProps } from '../cards/types';
import PollSettings from './PollSettings';

const PollModal = React.lazy(() => import('./PollModal'));
const AnalyticsPollCardBody = React.lazy(() => import('./AnalyticsPollCard'));

export class PollInfo extends InteractionInfo {
  public constructor(
    i: InteractionForAttendeeFragment | InteractionFragment,
    ewebinar?: PublicEWebinarFragment,
    attendee?: AttendeeFragment
  ) {
    super(i, ewebinar, attendee);
  }

  public hasExpiresInField(): boolean {
    return this.i.details?.resultsAppearAt === ResultsAppearAt.AfterXMinutes;
  }

  public hasShowTimerToggle(): boolean {
    return this.i.details?.resultsAppearAt === ResultsAppearAt.AfterXMinutes;
  }

  public showTimer(mode: PlayerMode): boolean {
    const endsIn = this.i.details?.endsIn;
    const showTimer = this.i.details?.showTimer;
    const appearAt = this.i.details?.resultsAppearAt;

    return (
      mode !== PlayerMode.replay &&
      Boolean(endsIn) &&
      Boolean(showTimer) &&
      appearAt === ResultsAppearAt.AfterXMinutes
    );
  }

  public hasAnswers(): boolean {
    return true;
  }

  public hasStickySetting(): boolean {
    return false;
  }

  public valueType(): InteractionValueType {
    return InteractionValueType.Answers;
  }

  public minRange(): number {
    return 2;
  }

  public maxRange(): number {
    return 6;
  }

  public icon(): React.FC<StyleProps> {
    return PollIcon;
  }

  public modalValidationRules(): ModalValidationRules {
    return {
      ...super.modalValidationRules(),
      answers: yup
        .array()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .when('publishAsTemplate', ([value]: boolean[], field: any) =>
          value
            ? field.of(
                yup.object({
                  text: yup.string().trim(),
                })
              )
            : field.of(
                yup.object({
                  text: yup.string().trim().required(),
                })
              )
        )
        .required()
        .min(
          this.minRange(),
          `You must have at least ${pluralize('promotion', this.minRange(), true)}`
        ),
    };
  }

  public attendeeCardValidationRules(): yup.AnyObjectSchema | undefined {
    return yup.object().shape({
      arrayAnswer: yup.array(yup.string()).required(),
      otherName: yup.string().nullable(),
      stringAnswer: yup.string().when(['arrayAnswer', 'otherName'], {
        is: (arrayAnswer: string[], otherName: string) => arrayAnswer?.indexOf(otherName) > -1,
        then: () => yup.string().required(),
      }),
    });
  }

  public useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null {
    return PollModal;
  }

  public modalSettingsAccordions(): InteractionModalAccordionsHook {
    return PollSettings;
  }

  public footerHeight(): number {
    return 4;
  }

  public shouldShowResultState(playbackPosition: number): boolean {
    switch (this.i.details?.resultsAppearAt) {
      case ResultsAppearAt.Immediately:
        return Boolean(this.reaction);

      case ResultsAppearAt.EndOfPresentation:
        return (
          playbackPosition >= this.rooms.getRoom(RoomType.Presentation).duration - 10 &&
          this.isResultsCard()
        ); // for additional second

      case ResultsAppearAt.AfterXMinutes: {
        const endIn = this.i.details?.endsIn ?? 0;
        const resultsAppearAtPlaybackPos = this.playbackPosition + endIn;
        return playbackPosition > resultsAppearAtPlaybackPos;
      }

      case ResultsAppearAt.Never:
      default:
        return false;
    }
  }

  public isExpired(playbackPosition: number, mode: PlayerMode): boolean {
    const endIn = this.fragment.details?.endsIn;
    if (
      typeof endIn === 'number' &&
      this.i.details?.resultsAppearAt === ResultsAppearAt.AfterXMinutes &&
      this.playbackPosition + endIn - playbackPosition <= 0
    ) {
      return true;
    }

    return super.isExpired(playbackPosition, mode);
  }

  public removeCardOnAnswer(): boolean {
    switch (this.i.details?.resultsAppearAt) {
      case ResultsAppearAt.Immediately:
        return false;

      case ResultsAppearAt.EndOfPresentation:
        return !this.isResultsCard();

      case ResultsAppearAt.AfterXMinutes:
        return !this.isResultsCard();

      case ResultsAppearAt.Never:
      default:
        return true;
    }
  }

  public cardDisplayState(playbackPosition: number, mode: PlayerMode): InteractionDisplayState {
    if (mode === PlayerMode.edit) {
      return InteractionDisplayState.Normal;
    }

    if (this.shouldShowResultState(playbackPosition)) {
      return InteractionDisplayState.Result;
    }

    if (this.reaction) {
      return InteractionDisplayState.Submitted;
    }

    return InteractionDisplayState.Normal;
  }

  public detailsDefault(interactions: InteractionInfo[]): InteractionDetails {
    return {
      ...super.detailsDefault(interactions),
      answers: new Array(this.minRange()).fill('').map((_) => {
        return { text: '', value: '' } as OptionField;
      }),
      resultsAppearAt: ResultsAppearAt.EndOfPresentation,
    };
  }

  public renderColumnData(data: RenderColumnData): RenderColumnData | null {
    return {
      ...data,
      width: this.i.details?.selectionType === SelectionType.Multi ? 240 : 120,
      orderBy: GetRegistrantsOrderBy.PollAnswer,
      cellRenderer: ({ cellData }: TableCellProps): JSX.Element => {
        const reaction = cellData as ReactionFragment;
        if (!reaction?.arrayAnswer) {
          return <div />;
        }
        let arrayAnswer = reaction.arrayAnswer ?? [];
        const otherOption = this.details?.answers?.find((a) => a.isOther === true);
        if (otherOption && reaction.stringAnswer && arrayAnswer.includes(otherOption.text)) {
          arrayAnswer = arrayAnswer.map((a) =>
            a === otherOption.text ? `${a}: ${reaction.stringAnswer}` : a
          );
        }
        return <div className='pre-wrap'>{arrayAnswer.join(', ')}</div>;
      },
    };
  }

  public hasPinToTopToggle(): boolean {
    return true;
  }
  public hasAnswerValue(): boolean {
    if (!this.i.details?.answers) {
      return false;
    }
    return Boolean(this.i.details?.answers.find((answer) => Boolean(answer.value)));
  }

  // Surface Text instead of Value for Chat Message
  public answerMessage(reaction: ReactionFragment): string {
    return surfaceAnswerText(this.i.details?.answers, reaction.arrayAnswer);
  }

  public analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>> {
    return AnalyticsPollCardBody;
  }

  public cardBodyComponent(): React.FC | React.LazyExoticComponent<React.FC<{}>> {
    return PollCard;
  }
}
